import {
  Badge,
  Button,
  ButtonIcon,
  EmptyText,
  ShellIconAlertCircle,
  ShellIconBell,
  ShellIconCheckCircle,
  ShellIconClipboardList,
  ShellIconUsers,
  ShellIconX,
  Spinner,
  useIsPhone,
} from '@zrsystem/ui';
import { useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react';

import type { StaffNotificationListItem } from '@/features/notifications/api/staff-notifications.schemas.js';
import { useStaffNotificationSocket } from '@/features/notifications/hooks/use-staff-notification-socket.js';
import {
  useDismissStaffNotification,
  useMarkStaffNotificationRead,
  useStaffNotifications,
  useStaffUnreadNotificationCount,
} from '@/features/notifications/hooks/use-staff-notifications.js';
import { parseStaffNotificationDeeplink } from '@/features/notifications/lib/parse-staff-notification-deeplink.js';
import '@/features/notifications/notifications.css';
import { HttpError } from '@/shared/api/types.js';
import { translateError } from '@/shared/i18n/error-messages.js';

const TYPE_ICON: Record<StaffNotificationListItem['type'], ReactNode> = {
  CHAT_MESSAGE: <ShellIconUsers size={14} />,
  RECEPTION_CHECKLIST_PENDING: <ShellIconClipboardList size={14} />,
  RECEPTION_CHECKLIST_APPROVED: <ShellIconCheckCircle size={14} />,
  RECEPTION_CHECKLIST_REJECTED: <ShellIconAlertCircle size={14} />,
  CHECKOUT_CHECKLIST_PENDING: <ShellIconClipboardList size={14} />,
  CHECKOUT_CHECKLIST_APPROVED: <ShellIconCheckCircle size={14} />,
  CHECKOUT_CHECKLIST_REJECTED: <ShellIconAlertCircle size={14} />,
  SERVICE_ORDER_CREATED: <ShellIconClipboardList size={14} />,
  SERVICE_ORDER_STATUS_CHANGED: <ShellIconClipboardList size={14} />,
  SERVICE_QUOTE_SENT: <ShellIconClipboardList size={14} />,
  SERVICE_ORDER_ITEM_DECISION: <ShellIconCheckCircle size={14} />,
  SERVICE_QUOTE_ITEM_DECISION: <ShellIconCheckCircle size={14} />,
  SERVICE_ORDER_ITEM_ADDED: <ShellIconClipboardList size={14} />,
  SERVICE_ORDER_ITEM_EXECUTION_CHANGED: <ShellIconClipboardList size={14} />,
  WORK_TIMER_CLOSING_MECHANIC: <ShellIconAlertCircle size={14} />,
  WORK_TIMER_CLOSING_SUMMARY: <ShellIconAlertCircle size={14} />,
};

function formatNotificationTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** Resolves the shell path for a notification deeplink (BR-NOT-05). */
export function resolveStaffNotificationPath(
  deeplink: string,
  paths: {
    conversations: string;
    checkInResume: (receptionId: string) => string;
    checkOutDetail: (serviceOrderId: string) => string;
    serviceOrderDetail: (orderId: string) => string;
    quoteDetail: (quoteId: string) => string;
    workTimerOvertimePath: (mechanicUserId: string) => string;
  },
): string | null {
  const parsed = parseStaffNotificationDeeplink(deeplink);
  if (parsed === null) {
    return null;
  }
  if (parsed.kind === 'conversation') {
    return paths.conversations;
  }
  if (parsed.kind === 'check-in') {
    return paths.checkInResume(parsed.receptionId);
  }
  if (parsed.kind === 'check-out') {
    return paths.checkOutDetail(parsed.serviceOrderId);
  }
  if (parsed.kind === 'service-order') {
    return paths.serviceOrderDetail(parsed.orderId);
  }
  if (parsed.kind === 'work-timer-overtime') {
    return paths.workTimerOvertimePath(parsed.mechanicUserId);
  }
  return paths.quoteDetail(parsed.quoteId);
}

export interface NotificationBellProps {
  readonly onNavigate: (path: string) => void;
  readonly conversationsPath: string;
  readonly checkInResumePath: (receptionId: string) => string;
  readonly checkOutDetailPath: (serviceOrderId: string) => string;
  readonly serviceOrderDetailPath: (orderId: string) => string;
  readonly quoteDetailPath: (quoteId: string) => string;
  readonly workTimerOvertimePath: (mechanicUserId: string) => string;
}

/** Shell bell + inbox popover (BR-NOT-07/08) — mirrors `ShellAccountMenu` positioning pattern. */
export function NotificationBell({
  onNavigate,
  conversationsPath,
  checkInResumePath,
  checkOutDetailPath,
  serviceOrderDetailPath,
  quoteDetailPath,
  workTimerOvertimePath,
}: NotificationBellProps): ReactElement {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const isPhone = useIsPhone();

  useStaffNotificationSocket(true);
  const unreadQuery = useStaffUnreadNotificationCount();
  const listQuery = useStaffNotifications({ enabled: open });
  const markRead = useMarkStaffNotificationRead();
  const dismiss = useDismissStaffNotification();

  const unreadCount = unreadQuery.data?.unreadCount ?? 0;
  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);
  const items = listQuery.data?.items ?? [];
  const errorMessage =
    listQuery.error instanceof HttpError ? translateError(listQuery.error.code) : null;

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function handleOpenItem(item: StaffNotificationListItem): void {
    setOpen(false);
    if (item.readAt === null) {
      markRead.mutate(item.id);
    }
    const path = resolveStaffNotificationPath(item.deeplink, {
      conversations: conversationsPath,
      checkInResume: checkInResumePath,
      checkOutDetail: checkOutDetailPath,
      serviceOrderDetail: serviceOrderDetailPath,
      quoteDetail: quoteDetailPath,
      workTimerOvertimePath,
    });
    if (path !== null) {
      onNavigate(path);
    }
  }

  return (
    <div ref={rootRef} className="staff-notification-bell">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        aria-label={unreadCount > 0 ? `Notificações, ${unreadCount} não lidas` : 'Notificações'}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={panelId}
        title="Notificações"
        className="staff-notification-bell__trigger"
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <ButtonIcon>
          <ShellIconBell size={18} />
        </ButtonIcon>
        {unreadCount > 0 ? (
          <Badge variant="solid-danger" className="staff-notification-bell__badge">
            {badgeLabel}
          </Badge>
        ) : null}
      </Button>

      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label="Notificações"
          className={`ctx-switcher-menu ctx-switcher-menu--viewport-end staff-notification-bell__panel${isPhone ? ' staff-notification-bell__panel--phone' : ''}`}
        >
          <div className="staff-notification-bell__header">
            <span className="staff-notification-bell__title">Notificações</span>
            {isPhone ? (
              <div className="staff-notification-bell__header-actions">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Fechar notificações"
                  className="zr-touch-target"
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  <ButtonIcon>
                    <ShellIconX size={14} />
                  </ButtonIcon>
                </Button>
              </div>
            ) : null}
          </div>

          <div className="staff-notification-bell__list">
            {listQuery.isPending ? (
              <div className="staff-notification-bell__state">
                <Spinner aria-label="Carregando notificações" />
              </div>
            ) : null}

            {errorMessage !== null ? (
              <div className="staff-notification-bell__state">
                <EmptyText>{errorMessage}</EmptyText>
              </div>
            ) : null}

            {!listQuery.isPending && errorMessage === null && items.length === 0 ? (
              <div className="staff-notification-bell__state">
                <EmptyText>Nenhuma notificação por aqui.</EmptyText>
              </div>
            ) : null}

            {items.map((item) => {
              const unread = item.readAt === null;
              return (
                <div key={item.id} className="staff-notification-bell__item" data-unread={unread}>
                  <button
                    type="button"
                    className="staff-notification-bell__item-main"
                    onClick={() => {
                      handleOpenItem(item);
                    }}
                    aria-label={unread ? `${item.title}, não lida` : item.title}
                  >
                    <span className="staff-notification-bell__item-icon">
                      {TYPE_ICON[item.type] ?? <ShellIconBell size={14} />}
                    </span>
                    <span className="staff-notification-bell__item-body">
                      <span className="staff-notification-bell__item-title">{item.title}</span>
                      <span className="staff-notification-bell__item-text">{item.body}</span>
                      <span className="staff-notification-bell__item-time">
                        {formatNotificationTime(item.createdAt)}
                      </span>
                    </span>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    iconOnly
                    aria-label="Remover notificação"
                    className="staff-notification-bell__item-dismiss"
                    onClick={() => {
                      dismiss.mutate(item.id);
                    }}
                  >
                    <ButtonIcon>
                      <ShellIconX size={12} />
                    </ButtonIcon>
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
